import {
  hasDistinctItemCharacteristics,
  type DeepPartial,
  type ItemMaterialCharacteristics,
  type MaterialCharacteristics,
  type ProductMaterialCharacteristics,
  type ProductType,
} from "@jasper/client-models";
import { Stack, Switch } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { FieldValueProps, ValidationErrors } from "../../types";
import { ItemsMaterialCharacteristicsForm } from "../items";
import { SolidarizedItemsMaterialCharacteristicsForm } from "../items";
import { MaterialCharacteristicsForm } from "../material";
import { useState } from "react";
import { handleDivBlur } from "../../utils";

type ItemsMaterialCharacteristics = Record<number, MaterialCharacteristics>;

interface Props extends FieldValueProps<ProductMaterialCharacteristics> {
  teeth: number[];
  productType: ProductType;
  isSolidarized?: boolean;
  itemMaterials: { value: string; label: string }[];
  itemShadeTypes: { value: string; label: string }[];
  itemShades: { value: string; label: string }[];
  defaultIsPerTooth?: boolean;
  disableIsPerTooth?: boolean;
  validateTooth: (
    tooth: number
  ) => ValidationErrors<MaterialCharacteristics> | undefined;
}

export const ProductMaterialCharacteristicsForm = ({
  value,
  errors,
  onChange,
  teeth,
  productType,
  isSolidarized = false,
  defaultIsPerTooth = false,
  onBlur,
  disableIsPerTooth,
  ...itemCharacteristicsProps
}: Props) => {
  const { t } = useTranslation("forms");

  const [isPerToothSelected, setIsPerToothSelected] =
    useState(defaultIsPerTooth);

  const isPerTooth =
    isPerToothSelected || hasDistinctItemCharacteristics(value?.items ?? []);

  const itemsMaterialCharacteristicsByTooth = teeth.reduce<
    DeepPartial<ItemsMaterialCharacteristics>
  >((acc, tooth) => {
    const found = value?.items?.find(item => item.tooth === tooth);
    if (!found?.materialCharacteristics) {
      return acc;
    }

    return {
      ...acc,
      [tooth]: found.materialCharacteristics,
    };
  }, {});

  const itemsByTooth = value?.items?.reduce<
    Record<number, DeepPartial<ItemMaterialCharacteristics>>
  >((acc, item) => {
    if (typeof item.tooth !== "number") {
      return acc;
    }

    return {
      ...acc,
      [item.tooth]: item,
    };
  }, {});

  const errorsByTooth = teeth.reduce<
    ValidationErrors<ItemsMaterialCharacteristics>
  >((acc, tooth) => {
    const itemIndex = value?.items?.findIndex(item => item.tooth === tooth);
    if (itemIndex === undefined || itemIndex < 0) {
      return acc;
    }

    const found = errors?.items?.[itemIndex];
    if (!found?.materialCharacteristics) {
      return acc;
    }

    return {
      ...acc,
      [tooth]: found.materialCharacteristics,
    };
  }, {});

  const mapUniformItemsArray = (
    materialCharacteristics: DeepPartial<MaterialCharacteristics> | undefined
  ): DeepPartial<ItemMaterialCharacteristics[]> => {
    return teeth.map(tooth => ({
      ...itemsByTooth?.[tooth],
      tooth,
      materialCharacteristics,
    }));
  };

  const mapToItemsArray = (
    map: DeepPartial<ItemsMaterialCharacteristics>
  ): DeepPartial<ItemMaterialCharacteristics[]> => {
    return teeth.map(tooth => ({
      ...itemsByTooth?.[tooth],
      tooth,
      materialCharacteristics: map?.[tooth],
    }));
  };

  const firstToothIndex = value?.items?.findIndex(
    item => item.tooth === teeth[0]
  );

  return (
    <Stack onBlur={event => handleDivBlur(event, onBlur)}>
      {teeth.length > 1 && (
        <Switch
          label={t("forms.product.labels.isPerTooth")}
          checked={isPerTooth}
          disabled={disableIsPerTooth}
          onChange={event => {
            const isChecked = event.currentTarget.checked;
            setIsPerToothSelected(isChecked);
            if (!isChecked) {
              // When switching back to non per tooth, we want to keep the first tooth characteristics if they exist, otherwise we reset to empty characteristics
              const firstTooth = teeth[0];
              onChange({
                ...value,
                items: mapUniformItemsArray(
                  itemsMaterialCharacteristicsByTooth?.[firstTooth]
                ),
              });
            } else {
              onChange({
                ...value,
                items: value?.items?.map(item => ({
                  ...item,
                  materialCharacteristics: undefined,
                })),
              });
            }
          }}
        />
      )}

      {!isPerTooth && (
        <MaterialCharacteristicsForm
          value={
            value?.items?.find(item => item.tooth === teeth[0])
              ?.materialCharacteristics
          }
          errors={
            typeof firstToothIndex === "number" && firstToothIndex >= 0
              ? errors?.items?.[firstToothIndex]?.materialCharacteristics
              : undefined
          }
          onChange={materialCharacteristics =>
            onChange({
              ...value,
              items: mapUniformItemsArray(materialCharacteristics),
            })
          }
          {...itemCharacteristicsProps}
        />
      )}

      {isPerTooth && !isSolidarized && (
        <ItemsMaterialCharacteristicsForm
          value={itemsMaterialCharacteristicsByTooth}
          errors={errorsByTooth}
          onChange={next =>
            onChange({ ...value, items: mapToItemsArray(next) })
          }
          teeth={teeth}
          productType={productType}
          {...itemCharacteristicsProps}
        />
      )}

      {isPerTooth && isSolidarized && (
        <SolidarizedItemsMaterialCharacteristicsForm
          value={itemsMaterialCharacteristicsByTooth}
          errors={errorsByTooth}
          onChange={next =>
            onChange({ ...value, items: mapToItemsArray(next) })
          }
          teeth={teeth}
          productType={productType}
          {...itemCharacteristicsProps}
        />
      )}
    </Stack>
  );
};
